<template>
    <CqModal
        :showModal="show"
        :heading="comment?.id ? 'Bewerk de roosteropmerking' : 'Voeg nieuwe roosteropmerking toe'"
        :preventClosing="saving"
        @closeModal="closeModal"
    >
        <template v-if="comment">
            <CustomDateSelect v-model="comment.beginDate" label="Van" />
            <CustomDateSelect v-model="comment.endDate" label="Tot en met" />
            <CqTextInput v-model="comment.comment" :maxlength="5120" label="Opmerking" />
            <template v-if="comment.rosterIds!.length > 1">
                <CqToggle
                    v-model="currentRosterOnly"
                    label="Alleen voor het huidige rooster aanpassen"
                />
                <template v-if="!currentRosterOnly">
                    <br />
                    Let op: deze wijziging wordt doorgevoerd voor de volgende roosters:
                    <ul>
                        <li v-for="rosterId in comment.rosterIds" :key="rosterId">
                            {{ rosterNames[rosterId] ?? 'Onbekend rooster' }}
                        </li>
                    </ul>
                </template>
            </template>
            <CqButtonBar>
                <CqButton @click="closeModal" :disabled="saving" label="Annuleren" />
                <CqButton primary @click="save" :disabled="saving || !valueIsValid" label="Opslaan" icon="save" />
            </CqButtonBar>
        </template>
    </CqModal>
</template>

<script lang="ts">
    import {Component, Vue, Prop} from 'vue-facing-decorator';
    import {CqButton, CqButtonBar, CqModal, CqTextInput, CqToggle} from '@cquential/ui';
    import {ApiClient, RosterComment} from '@cquential/api';
    import CustomDateSelect from '@/components/Branding/CustomDateSelect.vue';
    import Notify from '@/components/modals/Notify.vue';

    @Component({
        components: {
            CqButton,
            CqButtonBar,
            CqModal,
            CqTextInput,
            CqToggle,
            CustomDateSelect,
        },
        emits: [
            'save:comment',
            'delete:comment',
        ],
    })
    export default class RosterCommentModal extends Vue {
        private static instance: RosterCommentModal | null;

        @Prop({type: Number, required: true}) public currentRosterId!: number;
        @Prop({required: true}) public rosterNames!: Record<number, string>;

        public show: boolean = false;
        public saving: boolean = false;
        public originalComment: RosterComment | null = null;
        public comment: RosterComment | null = null;
        public currentRosterOnly: boolean = false;

        public mounted(): void {
            RosterCommentModal.instance = this;
        }

        public beforeUnmount(): void {
            RosterCommentModal.instance = null;
        }

        public get valueIsValid(): boolean {
            return (this.comment?.comment?.length ?? 0) < 5120;
        }

        private closeModal(): void {
            this.show = false;
            this.comment = null;
            this.originalComment = null;
        }

        private async save(): Promise<void> {
            if (!this.valueIsValid || !this.comment) {
                return;
            }

            if (!this.comment.id && this.comment.comment! === '') {
                this.closeModal();

                return;
            }

            this.saving = true;

            const commentToSave: RosterComment = {...this.comment};
            const promises: Promise<void>[] = [];

            try {
                if (this.currentRosterOnly) {
                    commentToSave.rosterIds = [this.currentRosterId];
                    commentToSave.id = undefined;

                    promises.push(this.saveOriginalRosterCommentWithoutCurrentRoster());
                    if (commentToSave.comment !== '') {
                        promises.push(this.saveRosterComment(commentToSave));
                    }
                } else if (commentToSave.id && commentToSave.comment === '') {
                    await ApiClient.instance.deleteRosterComment(commentToSave.id);
                    this.$emit('delete:comment', commentToSave);
                } else {
                    promises.push(this.saveRosterComment(commentToSave));
                }

                if (promises.length > 0) {
                    await Promise.all(promises);
                }

                this.closeModal();
            } catch {
                /** @see Notify.createNotification() */
                // @ts-expect-error TS2339
                Notify.__vfdConstructor.createNotification({text: 'Kon de wijziging niet opslaan. Probeer het opnieuw.', type: 'error'});
            } finally {
                this.saving = false;
            }
        }

        private async saveOriginalRosterCommentWithoutCurrentRoster(): Promise<void> {
            const clonedComment: RosterComment = {
                ...this.originalComment!,
                rosterIds: this.originalComment!.rosterIds!.filter((rosterId: number): boolean => rosterId !== this.currentRosterId),
            };
            await ApiClient.instance.saveRosterComment(clonedComment);
            // remove the comment from comments of this roster
            this.$emit('delete:comment', this.originalComment!);
        }

        private async saveRosterComment(comment: RosterComment): Promise<void> {
            const result: number = await ApiClient.instance.saveRosterComment(comment);
            if (result) {
                comment.id = result;
            }
            this.$emit('save:comment', comment);
        }

        public static openModal(openComment: RosterComment): void {
            this.instance!.show = true;
            this.instance!.currentRosterOnly = false;
            this.instance!.originalComment = openComment;
            this.instance!.comment = {...openComment};
        }
    }
</script>
